import { useCallback, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { callQuery, GET_LATEST_VERSIONS_FOR_CSN, PATIENT_VISITS_BY_MRN_WITH_SUMMARY, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../../amplify/appsync/query";
import { PatientVisit } from "../../../../amplify/appsync/search-patients";

import { callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../../amplify/appsync/dashboard-llm";
import useSetPagePatientVisit from "../../../common/GlobalOptionsContext";

import { ExplainAdmitDCDashboard } from "../../../llm-visit/dashboard/ExplainAdmitDCDashboard";
import { ExplainEDDDashboard } from "../../../llm-visit/dashboard/ExplainEDDDashboard";
import { ExplainRevisitDashboard } from "../../../llm-visit/dashboard/ExplainRevisitDashboard";
import { EXPLAIN_REVIEW_ADMIT_DC, EXPLAIN_REVIEW_EDD, EXPLAIN_REVIEW_REVISIT } from "../../../../amplify/appsync/write";

export const JOB_CONFIG = [
    {
        key: EXPLAIN_REVIEW_ADMIT_DC,
        file: EXPLAIN_REVIEW_ADMIT_DC,
    },
    {
        key: EXPLAIN_REVIEW_REVISIT,
        file: EXPLAIN_REVIEW_REVISIT,
    },
    {
        key: EXPLAIN_REVIEW_EDD,
        file: EXPLAIN_REVIEW_EDD,
    },
];

export const AssessmentDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    unreadFileVersionMap?: Record<string, PatientFileVersion>,
    setUnreadFileVersionMap?: (map: Record<string, PatientFileVersion>) => void,
}) => {
    let { internalCSN } = useParams();
    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});
    const patientVisit = props.patientVisit;
    useSetPagePatientVisit(patientVisit);

    const [pastPatientVisits, setPastPatientVisits] = useState<PatientVisit[]>([]);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    useEffect(() => {
        if (!patientVisit) return;
        
        callQuery({
            mrn: patientVisit.mrn,
            operation: PATIENT_VISITS_BY_MRN_WITH_SUMMARY,
        }).then((patients: (PatientVisit)[]) => {
            console.log("callSearchPatients returned for internalCSN: " + patientVisit.internal_csn);
            setPastPatientVisits(patients);
        });

        callGetDashboardLLMStatus(patientVisit.csn).then((statuses) => {
            setStatuses(statuses);
        });

        callQuery({
            operation: GET_LATEST_VERSIONS_FOR_CSN,
            csn: patientVisit.csn,
        }).then((latestVersions: PatientFileVersion[]) => {
            const fileTypeToVersion: any = {};
            for (let latestVersion of latestVersions) {
                fileTypeToVersion[latestVersion.file_type] = latestVersion;
            }
            setLatestFileVersions(fileTypeToVersion);
        });
    }, [patientVisit]);

    useEffect(() => {
        if (!patientVisit) return;
        if (!props.unreadFileVersionMap) return;

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: patientVisit.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            props.setUnreadFileVersionMap?.(fileVersionMap);
        });
    }, [patientVisit]);

    /**
     * Called when the user selects another version of the LLM section
     */
    const handleFileVersionChange = useCallback((fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        })
        setLatestFileVersions(updatedFileVersions);
    }, [latestFileVersions]);

    return (
        <Container className="notes-dashboard">
            <Row className="mb-3">
                <Col>
                    <span className="gradient-text" style={{fontSize: '1.25rem'}}>Assessments</span>
                </Col>
                <Col className="text-right">
                </Col>
            </Row>

            <Row>
                <Col>
                    {patientVisit && <ExplainEDDDashboard 
                        tenant={props.tenant} patientVisit={patientVisit}
                        statuses={statuses} setStatuses={setStatuses} latestFileVersions={latestFileVersions} handleFileVersionChange={handleFileVersionChange}
                        unreadFileVersionMap={props.unreadFileVersionMap}
                    />}
                </Col>
            </Row>

            <Row>
                <Col>
                    {patientVisit && <ExplainAdmitDCDashboard 
                        tenant={props.tenant} patientVisit={patientVisit}
                        statuses={statuses} setStatuses={setStatuses} latestFileVersions={latestFileVersions} handleFileVersionChange={handleFileVersionChange}
                        unreadFileVersionMap={props.unreadFileVersionMap}
                    />}
                </Col>
            </Row>

            <Row>
                <Col>
                    {patientVisit && <ExplainRevisitDashboard 
                        tenant={props.tenant} patientVisit={patientVisit} pastPatientVisits={pastPatientVisits}
                        statuses={statuses} setStatuses={setStatuses} latestFileVersions={latestFileVersions} handleFileVersionChange={handleFileVersionChange}
                        unreadFileVersionMap={props.unreadFileVersionMap}
                    />}
                </Col>
            </Row>
        </Container>
    );
};
